// --- Core ---
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
// --- SaniQ ---
import {OmitSerialized} from "../../../models/Helpers";
import {IncomingEcgCount} from "../../../models/patient/measurement/ECG";
import {TaskCount} from "../../../models/task/Count";
import {Priority} from "src/modules/tasks/enums/Priority";

interface IndicatorsState {
  //Adherence events that are not yet marked as seen
  adherenceCount: number;
  //Sum of the open survey, measurement and adherence event counts
  eventsCount: number;
  incomingECGsCount: OmitSerialized<IncomingEcgCount>;
  //Unread messages for the physician
  messagesCount: number;
  tasksCount: OmitSerialized<TaskCount>;
  //Sum of open measurement and survey events
  triggerEventsCount: number;
}

const initialState: IndicatorsState = {
  adherenceCount: 0,
  eventsCount: 0,
  incomingECGsCount: {notSeen: 0},
  messagesCount: 0,
  tasksCount: {
    assignedCount: {
      [Priority.Blocker]: 0,
      [Priority.High]: 0,
      [Priority.Medium]: 0,
      [Priority.Low]: 0,
      [Priority.NoPriority]: 0
    },
    notStartedCount: 0,
    activeCount: 0,
    completedCount: 0,
    failedCount: 0
  },
  triggerEventsCount: 0
};

interface EventCountsPayload {
  adherenceCount: number;
  count: number;
  measurementCount: number;
  surveyCount: number;
}

const indicatorsSlice = createSlice({
  // eslint-disable-next-line formatjs/no-literal-string-in-object
  name: "indicators",
  initialState,
  reducers: {
    decreaseAdherenceCount(state, action: PayloadAction<number>) {
      state.adherenceCount = Math.max(state.adherenceCount - action.payload, 0);
    },
    decreaseEventsCount(state, action: PayloadAction<number>) {
      state.eventsCount = Math.max(state.eventsCount - action.payload, 0);
      state.triggerEventsCount = Math.max(state.triggerEventsCount - action.payload, 0);
    },
    decreaseMessagesCount(state, action: PayloadAction<number>) {
      state.messagesCount = Math.max(state.messagesCount - action.payload, 0);
    },
    decrementTaskCount(state, action: PayloadAction<Priority>) {
      const newCount = Math.max(state.tasksCount.assignedCount[action.payload] - 1, 0);
      state.tasksCount = {...state.tasksCount, assignedCount: {...state.tasksCount.assignedCount, [action.payload]: newCount}};
    },
    increaseEventsCount(state, action: PayloadAction<number>) {
      state.eventsCount = Math.max(state.eventsCount + action.payload, 0);
    },
    increaseMessagesCount(state, action: PayloadAction<number>) {
      state.messagesCount = Math.max(state.messagesCount + action.payload, 0);
    },
    setEventsCount(state, action: PayloadAction<EventCountsPayload>) {
      state.eventsCount = action.payload.count;
      state.adherenceCount = action.payload.adherenceCount;
      state.triggerEventsCount = action.payload.measurementCount + action.payload.surveyCount;
    },
    setIncomingECGsCount(state, action: PayloadAction<IncomingEcgCount>) {
      state.incomingECGsCount = action.payload;
    },
    setMessagesCount(state, action: PayloadAction<number>) {
      state.messagesCount = action.payload;
    },
    setTasksCount(state, action: PayloadAction<TaskCount>) {
      state.tasksCount = action.payload;
    }
  }
});

export const {
  decreaseAdherenceCount,
  decreaseEventsCount,
  decreaseMessagesCount,
  decrementTaskCount,
  increaseEventsCount,
  increaseMessagesCount,
  setEventsCount,
  setIncomingECGsCount,
  setMessagesCount,
  setTasksCount
} = indicatorsSlice.actions;

export default indicatorsSlice.reducer;
